import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LenderClientAssociation } from '@components/lender-card/client-lender-card/client-lender-card.component';
import { LenderSelectorComponent } from '@components/lender-selector/lender-selector.component';
import { clientLegalName } from '@core/util/client';
import { ICON } from '@core/util/icon';
import { getMortgageLenders } from '@core/util/mortgage';
import { AlgoliaLawFirmEmployee } from '@fnModels/algolia/lawfirm';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { BusinessClient, PersonClient } from '@models/clients';
import { LawFirmEmployeeType } from '@models/lawFirms';
import { LawFirm } from '@models/lawFirms';
import { MatterFileType } from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgageWithAssociations,
	MortgagePercentageOfInterestTypes,
	TransactionType,
} from '@models/mortgage';
import {
	CorrespondenceType,
	Lender,
	LenderArgs,
	LenderClientType,
	LenderClientTypeArgs,
	LenderWithAssociations,
	MortgageeType,
	PrivateLender,
} from '@models/lender';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { UrlService } from '@services/url/url.service';
import { BusinessState } from '@state/business';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { cloneDeep } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownOptions,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { EditingMortgageReLineDialogComponent } from '../editing-dialog/editing-mortgage-reline-dialog.component';

enum InstitutionType {
	INSTITUTION = 'institution',
	PRIVATE_LENDER = 'privateLender',
}

export interface CorrespondenceDropdownOption {
	id?: string;
}

const InstitutionTypeOptions: RadioGroupItem<InstitutionType>[] = [
	{ label: 'Institution', value: InstitutionType.INSTITUTION },
	{ label: 'Private lender', value: InstitutionType.PRIVATE_LENDER },
];

const CorrespondenceTypeOptions: RadioGroupItem<CorrespondenceType>[] = [
	{ label: 'Lender(s)', value: CorrespondenceType.LENDERS },
	{ label: 'Solicitor', value: CorrespondenceType.SOLICITOR },
];

@UntilDestroy()
@Component({
	selector: 'app-manual-mortgage-lender-details',
	templateUrl: './lender-details.component.html',
	styleUrls: ['./lender-details.component.scss'],
	standalone: false,
})
export class ManualMortgageLenderDetailsComponent implements OnInit {
	public institutionTypeOptions = InstitutionTypeOptions;
	public selectedInstitutionType = InstitutionType.INSTITUTION;
	public contactCardDropdownItems = [
		{
			icon: ICON.OVERVIEW,
			id: '1',
			name: 'View global',
		},
	];

	public ICON = ICON;
	public _mortgage: MatterMortgageWithAssociations;
	public _showLenderSelector = false;
	public defaultMortgageReline = '';
	public parsedMortgageReline = '';
	public showParsedMortgageReline = true;
	public showCorrespondenceDropdownOptions = false;
	public selectedCorrespondenceDropdownOption: CorrespondenceDropdownOption;
	public MatterMortgageInstructionSource = MatterMortgageInstructionSource;

	public correspondenceTypeOptions = CorrespondenceTypeOptions;
	public correspondenceTypes = CorrespondenceType;

	public collapsedCardStates: { [key: string]: boolean } = {};
	public parseMortgageRelineSubscription: Subscription;
	public legacyPrivateLender: PrivateLender;
	public legacyPrivateLenderTooltip =
		'This lender is not synced with a global profile. Please remove and add the lender again.';

	public percentageOfInterestTypes = MortgagePercentageOfInterestTypes;
	public percentageOfInterestOptions: DropdownOptions<MortgagePercentageOfInterestTypes> =
		[
			{
				name: 'Capacity per lender',
				value: MortgagePercentageOfInterestTypes.CAPACITY_PER_CLIENT,
			},
		];

	public percentageOfInterestSelectedLabel: string = null;

	public lenders: LenderWithAssociations[] = [];

	public _addLenderDisabled = true;

	@ViewChild(LenderSelectorComponent, { static: false })
	lenderSelectorComponent: LenderSelectorComponent;

	@Input() set mortgage(mortgage: MatterMortgageWithAssociations) {
		this.percentageOfInterestSelectedLabel =
			this.percentageOfInterestOptions.find(
				(option) => option.value === mortgage?.percentageOfInterestType,
			)?.name;

		if (!this._mortgage) {
			if (this.legacyPrivateLender) {
				this._addLenderDisabled = true;
			} else {
				this._addLenderDisabled = false;
			}
		}

		this._mortgage = cloneDeep(mortgage);
		this.showParsedMortgageReline = true;
		this.legacyPrivateLender = this._mortgage?.privateLender;

		this.lenders = getMortgageLenders(
			this._mortgage,
		) as LenderWithAssociations[];
		const lenders = Object.keys(this._mortgage?.lenders ?? {});
		lenders.forEach((key) => {
			this.collapsedCardStates[key] = this.collapsedCardStates[key] ?? true;
		});

		this.isVTBMortgage =
			this._mortgage?.transactionType === TransactionType.VTB;
	}
	@Input() type: 'light' | 'dark' = 'dark';
	@Input() subtitle = '';
	@Input() subtitleClasses = '';

	@Output() onLenderRemoved = new EventEmitter<string>();
	@Output() onLenderSetAsPrimary = new EventEmitter<string>();
	@Output() onLenderUpdated = new EventEmitter<Lender>();
	@Output() onLenderAdded = new EventEmitter<LenderArgs>();
	@Output() onLenderAssociationAdded =
		new EventEmitter<LenderClientAssociation>();
	@Output() onRemoveLenderInvestor = new EventEmitter<{
		lenderId: string;
		investorId: string;
	}>(); //Financial institutions only
	@Output() onCorrespondenceSolicitorUpdate = new EventEmitter<LawFirm>();
	@Output() onCorrespondenceRemove = new EventEmitter<void>();
	@Output() onPercentageOfInterestTypeUpdated =
		new EventEmitter<MortgagePercentageOfInterestTypes>();
	@Output() onLenderPercentageOfInterestDetailsUpdated = new EventEmitter<{
		lenderId: string;
		percentageOfInterest: string;
		percentageOfInterestType: MortgagePercentageOfInterestTypes;
	}>();

	@Select(TagsSelector.getTagInjectedDocument)
	parsedDocumentFilterFn$: Observable<(doc: string) => string>;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Select(MatterClients.get_vtbLenderCapableClients)
	vtbCapableClients$: Observable<Clients[]>;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;
	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	public sellerDropdownOptions: DropdownOption<
		PersonClient | BusinessClient
	>[] = [];
	public isVTBMortgage = false;
	public addVendorUrl: string;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.vtbCapableClients$.pipe(untilDestroyed(this)).subscribe((clients) => {
			this.sellerDropdownOptions = clients.map((client) => ({
				name: clientLegalName(client, this.nameFormattingSettings),
				value: client,
			}));

			if (
				this.isVTBMortgage &&
				((this.sellerDropdownOptions.length === 0 &&
					this.lenders.length === 0) ||
					(this.lenders.length > 0 && clients.length === 0))
			) {
				this._addLenderDisabled = true;
			} else {
				this._addLenderDisabled = false;
			}
		});

		if (this.fileType === MatterFileType.SALE) {
			this.addVendorUrl = UrlService.Matter_Parties(this.matterId);
		} else if (this.fileType === MatterFileType.PURCHASE) {
			this.addVendorUrl = UrlService.Matter_Parties_OtherParties(this.matterId);
		}
	}

	editMortgageReLine() {
		this.editText(this._mortgage.id, this._mortgage.reLine);
	}

	toggleShowCorrespondenceDropdownOptions() {
		this.showCorrespondenceDropdownOptions =
			!this.showCorrespondenceDropdownOptions;
	}

	hideCorrespondenceDropdownOptions() {
		this.showCorrespondenceDropdownOptions = false;
	}

	_onLenderRemoved(lenderId: string) {
		this.onLenderRemoved.emit(lenderId);
		delete this.collapsedCardStates[lenderId];
		const keys = Object.keys(this.collapsedCardStates);
		if (keys.length === 1) {
			this.collapsedCardStates[keys[0]] = true;
		}
	}

	_onLenderAdded(lenderArgs: LenderArgs) {
		this.onLenderAdded.emit(lenderArgs);
		this.hideLenderSelector();
	}

	_onClientAddedAsLender(client: PersonClient | BusinessClient) {
		const lender: Partial<LenderClientType> = {
			clientId: client.id,
		};

		const args: LenderClientTypeArgs = {
			isPerson: !client.leap?.cardId,
			alreadyAddedToMatter: false,
			leap: client.leap,
		};

		this._onLenderAdded({
			lender: {
				...lender,
				mortgageeSubType: client.type,
				mortgageeType: MortgageeType.PRIVATE_LENDER,
			},
			args: args,
		});
	}

	setCorrespondenceType(type: CorrespondenceType) {
		this.mortgageDispatch.SetCorrespondenceType(this._mortgage.id, type);
	}

	selectLawFirm(selectedlLawfirmId: string) {
		this.mortgageDispatch.SetCorrespondenceSolicitor(
			this._mortgage.id,
			selectedlLawfirmId,
		);
	}

	selectLawyer(selectedLawyer: AlgoliaLawFirmEmployee) {
		this.selectedCorrespondenceDropdownOption = null;
		this.mortgageDispatch.SetCorrespondenceSolicitorEmployee(
			this._mortgage.id,
			selectedLawyer.lawFirmId,
			selectedLawyer.id,
			LawFirmEmployeeType.LAWYER,
		);
	}

	removeCorrespondence() {
		this.mortgageDispatch.RemoveCorrespondence(this._mortgage.id);
	}

	toggleLenderSelector() {
		this._showLenderSelector = !this._showLenderSelector;
	}

	hideLenderSelector() {
		this._showLenderSelector = false;
	}

	showLenderSelector() {
		this._showLenderSelector = true;
	}

	onAddAssociation(association: LenderClientAssociation) {
		this.onLenderAssociationAdded.emit(association);
	}

	onRemoveInvestor(lenderId: string, investorId: string) {
		this.onRemoveLenderInvestor.emit({ lenderId, investorId });
	}

	trackLenderById(index, value) {
		return value.id;
	}

	getParsedDocument$(document: string) {
		return this.parsedDocumentFilterFn$.pipe(
			untilDestroyed(this),
			map((filter) => filter(document)),
		);
	}

	onLenderCardExpanded(collapsedState: boolean, lenderId: string) {
		Object.keys(this.collapsedCardStates).forEach((key) => {
			this.collapsedCardStates[key] = key === lenderId ? collapsedState : true;
		});
	}

	deleteLegacyPrivateLender() {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			privateLender: null,
		} as unknown as Partial<MatterMortgage>);
	}

	onPercentageInterestTypeSelected(
		type: DropdownResult<DropdownOption<MortgagePercentageOfInterestTypes>>,
	) {
		this.onPercentageOfInterestTypeUpdated.emit(type.result.value);
		this.percentageOfInterestSelectedLabel = type.result.name;
	}

	clientAsLenderDropdownSelect(
		client: DropdownResult<DropdownOption<PersonClient | BusinessClient>>,
	) {
		if (client.group === 0) {
			this.sellerDropdownOptions.forEach((client) => {
				this._onClientAddedAsLender(client.value);
			});
		} else {
			this._onClientAddedAsLender(client.result.value);
		}
	}

	setActingForMortgagee(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			actingForMortgagee: value,
		});
	}

	setMMS(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { mms: value });
	}

	editText(mortgageId: string, tagValue: string): void {
		this.dialog.open(EditingMortgageReLineDialogComponent, {
			data: {
				mortgageId,
				tagValue,
			},
			maxHeight: '90vh',
			maxWidth: '90vw',
			width: '40vw',
		});
	}
}
